import BreadCrumb from "Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import Loader from "Common/Loader";
import InProgressMatchResult from "Common/Tables/InProgressMatchResult";
import MatchResultTable from "Common/Tables/MatchResultTable";
import { useEffect, useState } from "react";
import {
  Container,
  Button,
  Card,
  Form,
  Row,
  Col,
  Nav,
  Tab,
} from "react-bootstrap";
import Select from "react-select";
import { settingServices } from "Utils/setting/services";
import { currentDateTime } from "Utils/datefilter";

const MatchResult = () => {
  const [showFilter, setFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState<string | Date>("");
  const [endDate, setEndDate] = useState<string | Date>("");
  const [eventListData, setEventListData] = useState<LsitResponse>();
  const [customSelectOptions, setCustomSelectOptions] = useState<any[]>([]);
  const [customSelectSeries, setCustomSelectSeries] = useState<any[]>([]);
  const [customSelectMatch, setCustomSelectMatch] = useState<any[]>([]);
  const [customSelectMarket, setCustomSelectMarket] = useState<any[]>([]);
  const [pendingData, setPendingData] = useState<pendingResponse[]>([]);

  const [tempSport, setTempSport] = useState<any>(null);
  const [tempSeries, setTempSeries] = useState<any>(null);
  const [tempMatch, setTempMatch] = useState<any>(null);
  const [tempMarket, setTempMarket] = useState<any>(null);
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState<number>(1);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({
    total: 1,
    page: 1,
  });

  const [activeTab, setActiveTab] = useState("matchResult");

  const getPendingMarket = async () => {
    const { response } = await settingServices.getPendingMarket();
    setIsLoading(false);
    setPendingData(response?.data?.data);
  };

  useEffect(() => {
    getPendingMarket();
  }, []);

  const getEventList = async (
    sport: any,
    series: any,
    match: any,
    market: any
  ) => {
    const searchPayload: any = {};
    if (sport) searchPayload.sport_id = sport?.value;
    if (series) searchPayload.series_id = series?.value;
    if (match) searchPayload.match_id = match?.value;
    if (market) searchPayload.market_id = market?.value;

    const { response } = await settingServices.getEventList({
      type: "matchResult",
      search: searchPayload,
    });
    setIsLoading(false);
    setEventListData(response?.data[0]);
  };

  const getMarketResult = async (
    sport: any,
    series: any,
    match: any,
    market: any,
    userLimit?: number,
    userPage?: number
  ) => {
    const searchPayload: any = {};
    if (sport) searchPayload.sport_id = sport?.value;
    if (series) searchPayload.series_id = series?.value;
    if (match) searchPayload.match_id = match?.value;
    if (market) searchPayload.market_id = market?.value;

    const payload: any = {
      limit: userLimit || limit,
      page: userPage || page,
      search: searchPayload,
    };

    if (startDate) payload.from_date = startDate;
    if (endDate) payload.to_date = endDate;
    const { response } = await settingServices.getPendingMarket(payload);
    // const { response } = await settingServices.getMarketResult(payload);
    setPendingData(response?.data?.data);
    setListTotal({
      total: response?.data?.metadata[0]
        ? response?.data?.metadata[0]?.total / (userLimit || limit)
        : 1,
      page: response?.data?.metadata[0] ? response?.data?.metadata[0]?.page : 0,
    });
  };

  const handleSportChange = (selectedOption: any) => {
    setIsLoading(true);
    setTempSport(selectedOption);
    setTempSeries(null);
    setTempMatch(null);
    setTempMarket(null);
    getEventList(selectedOption, null, null, null);
    if (selectedOption) {
      setPage(1);
      getMarketResult(selectedOption, null, null, null, limit, page);
    } else {
      getPendingMarket();
    }
  };

  const handleSeriesChange = (selectedOption: any) => {
    setIsLoading(true);
    setTempSeries(selectedOption);
    setTempMatch(null);
    setTempMarket(null);
    getEventList(tempSport, selectedOption, null, null);
    if (tempSport || selectedOption) {
      setPage(1);
      getMarketResult(tempSport, selectedOption, null, null, limit, page);
    } else {
      getPendingMarket();
    }
  };

  const handleMatchChange = (selectedOption: any) => {
    setIsLoading(true);
    setTempMatch(selectedOption);
    setTempMarket(null);
    getEventList(tempSport, tempSeries, selectedOption, null);
    if (tempSport || tempSeries || selectedOption) {
      setPage(1);
      getMarketResult(tempSport, tempSeries, selectedOption, null, limit, page);
    } else {
      getPendingMarket();
    }
  };

  const handleMarketChange = (selectedOption: any) => {
    setIsLoading(true);
    setTempMarket(selectedOption);
    getEventList(tempSport, tempSeries, tempMatch, selectedOption);
    if (tempSport || tempSeries || tempMatch || selectedOption) {
      setPage(1);
      getMarketResult(
        tempSport,
        tempSeries,
        tempMatch,
        selectedOption,
        limit,
        1
      );
    } else {
      getPendingMarket();
    }
  };

  const handleSubmit = () => {
    setIsLoading(true);
    setPage(1);
    getEventList(tempSport, tempSeries, tempMatch, tempMarket);
    getMarketResult(tempSport, tempSeries, tempMatch, tempMarket, limit, 1);
  };

  const handleClear = () => {
    setIsLoading(true);
    setTempSport(null);
    setTempSeries(null);
    setTempMatch(null);
    setTempMarket(null);
    getPendingMarket();
  };

  useEffect(() => {
    if (eventListData?.sports) {
      const options = eventListData.sports.map((sport) => ({
        value: sport.sport_id,
        label: sport.sport_name,
      }));
      const series = eventListData.series.map((series) => ({
        value: series.series_id,
        label: series.series_name,
      }));
      const match = eventListData.matches.map((match) => ({
        value: match.match_id,
        label: match.match_name,
      }));
      const market = eventListData.markets.map((market) => ({
        value: market.market_id,
        label: market.market_name,
      }));
      setCustomSelectOptions(options);
      setCustomSelectSeries(series);
      setCustomSelectMatch(match);
      setCustomSelectMarket(market);
    }
  }, [eventListData]);

  useEffect(() => {
    getEventList(null, null, null, null);
  }, [limit]);

  const handlePageFilter = (page: number, limit: number) => {
    setIsLoading(true);
    setPage(page);
    setLimit(limit);
    getMarketResult(tempSport, tempSeries, tempMatch, tempMarket, limit, page);
  };

  const [resultProgressData, setResultProgressData] = useState<any[]>([]);

  const getResultProgress = async () => {
    try {
      const payload = {
        type: "RESULT",
        isFancy: false,
      };
      const { response } = await settingServices.getResultProgress(payload);
      setResultProgressData(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getResultProgress();
  }, []);

  const getPendingMarketFun = () => {
    getPendingMarket();
  };

  const getTodayDateTime = (hour = 0, minute = 0, second = 0) => {
    const now = new Date();
    now.setHours(hour, minute, second, 0);
    return now;
  };
  return (
    <div className="page-content">
      <Container fluid>
        <div className="position-relative mb-2 d-flex justify-content-between align-items-center">
          <BreadCrumb title="Match Result" pageTitle="Dashboard" back />
          <Button variant="subtle-dark" onClick={() => setFilter(!showFilter)}>
            <i className="fs-lg align-middle ri-filter-3-line"></i> Filter
          </Button>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k!)}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="matchResult">Match Result</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="inProgress">In Progress</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="matchResult">
              {showFilter && (
                <Card>
                  <Card.Body>
                    <Form>
                      <Row className="gy-3 align-items-end">
                        {/* <Col lg={9}>
                          <Row className="gap-3 gap-sm-0"> */}
                        <Col lg={3}>
                          <Form.Label>Start Date</Form.Label>
                          <Flatpickr
                            className="form-control"
                            pl
                            options={{
                              enableTime: true,
                              dateFormat: "Y-m-d H:i:s",
                              defaultDate: [startDate],
                              maxDate: endDate,
                            }}
                            value={startDate || getTodayDateTime(0, 0, 0)}
                            onChange={([date]: [Date]) => {
                              setStartDate(new Date(date));
                            }}
                          />
                        </Col>
                        <Col lg={3}>
                          <Form.Label>End Date</Form.Label>
                          <Flatpickr
                            className="form-control"
                            pl
                            options={{
                              enableTime: true,
                              dateFormat: "Y-m-d H:i:s",
                              defaultDate: [endDate],
                              maxDate: currentDateTime(),
                            }}
                            value={endDate || getTodayDateTime(23, 59, 59)}
                            onChange={([date]: [Date]) => {
                              setEndDate(new Date(date));
                            }}
                          />
                        </Col>
                        <Col lg={3}>
                          <Form.Label>Search Sport Name</Form.Label>
                          <Select
                            isClearable
                            options={customSelectOptions}
                            onChange={handleSportChange}
                            className="customSelect"
                            value={tempSport}
                          />
                        </Col>
                        <Col lg={3}>
                          <Form.Label>Search Series Name</Form.Label>
                          <Select
                            isClearable
                            options={customSelectSeries}
                            onChange={handleSeriesChange}
                            className="customSelect"
                            value={tempSeries}
                          />
                        </Col>
                        <Col lg={3}>
                          <Form.Label>Search Match Name</Form.Label>
                          <Select
                            isClearable
                            options={customSelectMatch}
                            onChange={handleMatchChange}
                            className="customSelect"
                            value={tempMatch}
                          />
                        </Col>
                        <Col lg={3}>
                          <Form.Label>Search Market Name</Form.Label>
                          <Select
                            isClearable
                            options={customSelectMarket}
                            onChange={handleMarketChange}
                            className="customSelect"
                            value={tempMarket}
                          />
                        </Col>
                        <Col lg={3}>
                          <Button
                            onClick={handleSubmit}
                            className="me-2 btn-sm"
                            style={{ height: "38px" }}
                          >
                            <i className="fs-lg align-middle ri-filter-line"></i>{" "}
                            Submit
                          </Button>
                          <Button
                            variant="outline-primary"
                            onClick={handleClear}
                            className="me-2 btn-sm"
                            style={{ height: "38px" }}
                          >
                            <i className="fs-lg align-middle ri-refresh-line"></i>{" "}
                            Clear
                          </Button>
                        </Col>
                        {/* </Row>
                        </Col> */}
                      </Row>
                    </Form>
                  </Card.Body>
                </Card>
              )}

              <Card>
                <Card.Body>
                  <MatchResultTable
                    reCall={getPendingMarketFun}
                    pendingData={pendingData}
                    page={page}
                    limit={limit}
                    listTotal={listTotal}
                    handlePageFilter={handlePageFilter}
                    getPendingMarket={getPendingMarket}
                    tempSport={tempSport}
                    tempSeries={tempSeries}
                    tempMatch={tempMatch}
                    tempMarket={tempMarket}
                  />
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="inProgress">
              <Card>
                <Card.Body>
                  <InProgressMatchResult
                    data={resultProgressData}
                    fancy={false}
                  />
                </Card.Body>
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {isLoading && <Loader />}
      </Container>
    </div>
  );
};

export default MatchResult;
